import React, { useState, useEffect, useCallback } from "react";
import {
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Server,
  Database,
  HardDrive,
  Cpu,
  Activity,
  Globe,
  ChevronDown,
  ChevronRight,
  Monitor,
  Terminal,
  Workflow,
  Radio,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const AUTH_URL = process.env.REACT_APP_AUTH_URL || "/auth";

// ─── Reusable components ────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const config = {
    healthy: {
      icon: CheckCircle,
      bg: "bg-green-100",
      text: "text-green-700",
      label: "Healthy",
    },
    running: {
      icon: CheckCircle,
      bg: "bg-green-100",
      text: "text-green-700",
      label: "Running",
    },
    active: {
      icon: CheckCircle,
      bg: "bg-green-100",
      text: "text-green-700",
      label: "Active",
    },
    unhealthy: {
      icon: XCircle,
      bg: "bg-red-100",
      text: "text-red-700",
      label: "Unhealthy",
    },
    unreachable: {
      icon: AlertTriangle,
      bg: "bg-yellow-100",
      text: "text-yellow-700",
      label: "Unreachable",
    },
    inactive: {
      icon: XCircle,
      bg: "bg-slate-100",
      text: "text-slate-500",
      label: "Inactive",
    },
    error: {
      icon: XCircle,
      bg: "bg-red-100",
      text: "text-red-700",
      label: "Error",
    },
  };
  const c = config[status] || config.unreachable;
  const Icon = c.icon;
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}
    >
      <Icon className="w-3.5 h-3.5 mr-1" />
      {c.label}
    </span>
  );
};

const ResponseTime = ({ ms }) => {
  if (ms == null) return null;
  const color =
    ms < 200
      ? "text-green-600"
      : ms < 1000
        ? "text-yellow-600"
        : "text-red-600";
  return <span className={`text-xs font-mono ${color}`}>{ms}ms</span>;
};

const isDown = (s) =>
  s === "unhealthy" || s === "unreachable" || s === "error" || s === "inactive";

const ErrorDetail = ({ error }) => {
  if (!error) return null;
  const text =
    typeof error === "object" ? JSON.stringify(error, null, 2) : String(error);
  return (
    <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-3">
      <div className="text-xs font-medium text-red-700 mb-1 flex items-center space-x-1">
        <Terminal className="w-3 h-3" />
        <span>Error Details</span>
      </div>
      <pre className="text-xs text-red-600 whitespace-pre-wrap break-all font-mono max-h-40 overflow-y-auto">
        {text}
      </pre>
    </div>
  );
};

// A service row that expands to show error details when down
const ServiceRow = ({ svc, showUrl }) => {
  const [expanded, setExpanded] = useState(false);
  const hasError = svc.error && isDown(svc.status);
  const clickable = hasError;

  return (
    <div
      className={`${clickable ? "cursor-pointer" : ""}`}
      onClick={() => clickable && setExpanded(!expanded)}
    >
      <div className="px-5 py-3 flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            {clickable &&
              (expanded ? (
                <ChevronDown className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
              ))}
            <span className="text-sm font-medium text-slate-700">
              {svc.name}
            </span>
            {svc.container && (
              <span className="text-xs text-slate-400 font-mono">
                {svc.container}
              </span>
            )}
            {svc.port && (
              <span className="text-xs text-slate-400">:{svc.port}</span>
            )}
          </div>
          {showUrl && svc.url && (
            <div className="text-xs text-slate-400 font-mono mt-0.5 truncate max-w-md ml-5">
              {svc.url}
            </div>
          )}
          {svc.method && (
            <div className="text-xs text-slate-400 font-mono mt-0.5 ml-5">
              <span
                className={`px-1 py-0.5 rounded text-[10px] font-bold mr-1 ${svc.method === "GET" ? "bg-blue-100 text-blue-600" : "bg-amber-100 text-amber-600"}`}
              >
                {svc.method}
              </span>
              {svc.url}
            </div>
          )}
        </div>
        <div className="flex items-center space-x-3 flex-shrink-0">
          <ResponseTime ms={svc.responseMs} />
          <StatusBadge status={svc.status} />
        </div>
      </div>
      {expanded && hasError && (
        <div className="px-5 pb-3">
          <ErrorDetail error={svc.error} />
        </div>
      )}
    </div>
  );
};

// ─── Section card ───────────────────────────────────────────────────

const SectionCard = ({
  icon: Icon,
  iconColor,
  title,
  badge,
  children,
  colSpan,
}) => (
  <div
    className={`bg-white rounded-lg shadow-md border border-slate-200 ${colSpan === 2 ? "col-span-2" : ""}`}
  >
    <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
      <div className="flex items-center space-x-2">
        <Icon className={`w-4 h-4 ${iconColor}`} />
        <h2 className="text-sm font-semibold text-slate-800">{title}</h2>
      </div>
      {badge}
    </div>
    {children}
  </div>
);

const SectionBadge = ({ items }) => {
  const down = (items || []).filter((i) => isDown(i.status)).length;
  const total = (items || []).length;
  if (down === 0)
    return (
      <span className="text-xs text-green-600 font-medium">
        {total}/{total} up
      </span>
    );
  return (
    <span className="text-xs text-red-600 font-medium">
      {down}/{total} down
    </span>
  );
};

// ─── Main dashboard ─────────────────────────────────────────────────

const PlatformDashboard = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${AUTH_URL}/platform-status`, { headers });
      if (res.status === 403) return; // non-admin — silently skip
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
      setLastRefresh(new Date());
    } catch (err) {
      console.error("Platform status fetch failed:", err);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchStatus, 60000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchStatus]);

  // Aggregate counts across all categories
  const allItems = data
    ? [
        ...(data.dockerServices || []),
        ...(data.hostServices || []),
        ...(data.infrastructure || []),
        ...(data.pinecone || []),
        ...(data.n8n ? [data.n8n] : []),
      ].filter(Boolean)
    : [];

  const healthyCount = allItems.filter((i) => !isDown(i.status)).length;
  const downCount = allItems.filter((i) => isDown(i.status)).length;
  const totalCount = allItems.length;
  const healthyPct = totalCount
    ? Math.round((healthyCount / totalCount) * 100)
    : 0;

  // Webhook counts
  const webhooks = data?.n8nWebhooks || [];
  const activeWebhooks = webhooks.filter((w) => w.status === "active").length;
  const downWebhooks = webhooks.filter((w) => isDown(w.status)).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Activity className="w-5 h-5 text-blue-600" />
          <h1 className="text-lg font-semibold text-slate-800">
            Platform Status
          </h1>
          {lastRefresh && (
            <span className="text-xs text-slate-400">
              Last checked: {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          {data && (
            <span className="text-xs text-slate-400 font-mono">
              ({data.totalMs}ms)
            </span>
          )}
        </div>
        <div className="flex items-center space-x-3">
          <label className="flex items-center space-x-2 text-xs text-slate-500 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="h-3.5 w-3.5 text-blue-600 rounded border-slate-300"
            />
            <span>Auto-refresh (60s)</span>
          </label>
          <button
            onClick={fetchStatus}
            disabled={loading}
            className="px-3 py-2 border border-slate-300 hover:bg-slate-50 rounded-lg text-sm flex items-center"
          >
            <RefreshCw
              className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`}
            />{" "}
            Refresh
          </button>
        </div>
      </div>

      {/* Summary cards */}
      {data && (
        <div className="grid grid-cols-6 gap-4">
          <div
            className={`rounded-lg p-4 border ${healthyPct === 100 ? "bg-green-50 border-green-200" : healthyPct > 50 ? "bg-yellow-50 border-yellow-200" : "bg-red-50 border-red-200"}`}
          >
            <div
              className={`text-3xl font-bold ${healthyPct === 100 ? "text-green-700" : healthyPct > 50 ? "text-yellow-700" : "text-red-700"}`}
            >
              {healthyPct}%
            </div>
            <div className="text-xs text-slate-500 mt-1">Overall Health</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-3xl font-bold text-green-700">
              {healthyCount}
            </div>
            <div className="text-xs text-green-600 mt-1">Services Up</div>
          </div>
          <div
            className={`border rounded-lg p-4 ${downCount > 0 ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200"}`}
          >
            <div
              className={`text-3xl font-bold ${downCount > 0 ? "text-red-700" : "text-slate-400"}`}
            >
              {downCount}
            </div>
            <div
              className={`text-xs mt-1 ${downCount > 0 ? "text-red-600" : "text-slate-400"}`}
            >
              Services Down
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-3xl font-bold text-blue-700">
              {activeWebhooks}/{webhooks.length}
            </div>
            <div className="text-xs text-blue-600 mt-1">Webhooks Active</div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-3xl font-bold text-blue-700">
              {data.stats?.users ?? "-"}
            </div>
            <div className="text-xs text-blue-600 mt-1">Users</div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-3xl font-bold text-blue-700">
              {data.stats?.facilities ?? "-"}
            </div>
            <div className="text-xs text-blue-600 mt-1">Facilities</div>
          </div>
        </div>
      )}

      {!data && loading && (
        <div className="text-center py-12 text-slate-500">
          Checking all services...
        </div>
      )}

      {data && (
        <div className="grid grid-cols-2 gap-6">
          {/* ═══ Docker Services ═══ */}
          <SectionCard
            icon={Server}
            iconColor="text-blue-600"
            title="Docker Services"
            badge={<SectionBadge items={data.dockerServices} />}
          >
            <div className="divide-y divide-slate-100">
              {(data.dockerServices || []).map((svc, i) => (
                <ServiceRow key={i} svc={svc} />
              ))}
              {(data.dockerServices || []).length === 0 && (
                <div className="px-5 py-4 text-sm text-slate-400 text-center">
                  No Docker services found.
                </div>
              )}
            </div>
          </SectionCard>

          {/* ═══ Host Services (non-Docker) ═══ */}
          <SectionCard
            icon={Monitor}
            iconColor="text-purple-600"
            title="Host Services (non-Docker)"
            badge={<SectionBadge items={data.hostServices} />}
          >
            <div className="divide-y divide-slate-100">
              {(data.hostServices || []).map((svc, i) => (
                <ServiceRow key={i} svc={svc} showUrl />
              ))}
              {(data.hostServices || []).length === 0 && (
                <div className="px-5 py-4 text-sm text-slate-400 text-center">
                  No host services configured.
                </div>
              )}
            </div>
          </SectionCard>

          {/* ═══ Infrastructure ═══ */}
          <SectionCard
            icon={HardDrive}
            iconColor="text-slate-600"
            title="Infrastructure"
            badge={
              <SectionBadge
                items={[
                  ...(data.infrastructure || []),
                  ...(data.n8n ? [data.n8n] : []),
                ]}
              />
            }
          >
            <div className="divide-y divide-slate-100">
              {(data.infrastructure || []).map((svc, i) => (
                <div key={i}>
                  <div className="px-5 py-3 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-slate-700 flex items-center space-x-2">
                        {svc.name === "PostgreSQL" && (
                          <Database className="w-3.5 h-3.5 text-blue-500" />
                        )}
                        {svc.name === "Redis" && (
                          <Cpu className="w-3.5 h-3.5 text-red-500" />
                        )}
                        {svc.name === "SFTP" && (
                          <HardDrive className="w-3.5 h-3.5 text-green-500" />
                        )}
                        <span>{svc.name}</span>
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5 font-mono">
                        {svc.host}:{svc.port}
                      </div>
                    </div>
                    <StatusBadge status={svc.status} />
                  </div>
                  {svc.error && isDown(svc.status) && (
                    <div className="px-5 pb-3">
                      <ErrorDetail error={svc.error} />
                    </div>
                  )}
                </div>
              ))}

              {/* N8N engine */}
              {data.n8n && (
                <div>
                  <div className="px-5 py-3 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Globe className="w-3.5 h-3.5 text-orange-500" />
                      <span className="text-sm font-medium text-slate-700">
                        {data.n8n.name || "N8N"}
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <ResponseTime ms={data.n8n.responseMs} />
                      <StatusBadge status={data.n8n.status} />
                    </div>
                  </div>
                  {data.n8n.error && isDown(data.n8n.status) && (
                    <div className="px-5 pb-3">
                      <ErrorDetail error={data.n8n.error} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </SectionCard>

          {/* ═══ Pinecone ═══ */}
          <SectionCard
            icon={Database}
            iconColor="text-purple-600"
            title="Pinecone Vector Indexes"
            badge={<SectionBadge items={data.pinecone} />}
          >
            <div className="divide-y divide-slate-100">
              {(data.pinecone || []).map((pc, i) => (
                <div key={i}>
                  <div className="px-5 py-3 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-slate-700">
                        {pc.name}
                      </div>
                      <div className="text-xs text-slate-400 font-mono mt-0.5 truncate max-w-xs">
                        {pc.host}
                      </div>
                      {pc.status === "healthy" && (
                        <div className="flex space-x-3 mt-1 text-xs text-slate-500">
                          <span>
                            {pc.totalVectors?.toLocaleString()} vectors
                          </span>
                          <span>{pc.dimension}d</span>
                          <span>{pc.namespaces} ns</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-3">
                      <ResponseTime ms={pc.responseMs} />
                      <StatusBadge status={pc.status} />
                    </div>
                  </div>
                  {pc.error && isDown(pc.status) && (
                    <div className="px-5 pb-3">
                      <ErrorDetail error={pc.error} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </SectionCard>

          {/* ═══ N8N Webhooks ═══ */}
          <SectionCard
            icon={Workflow}
            iconColor="text-orange-600"
            title="N8N Webhook Endpoints"
            colSpan={2}
            badge={
              <div className="flex items-center space-x-3">
                {downWebhooks > 0 && (
                  <span className="text-xs text-red-600 font-medium">
                    {downWebhooks} inactive
                  </span>
                )}
                <span className="text-xs text-green-600 font-medium">
                  {activeWebhooks}/{webhooks.length} active
                </span>
              </div>
            }
          >
            {data.n8nWebhooksError && (
              <div className="px-5 py-2 text-xs text-red-600 border-b border-slate-100">
                n8n API listing failed: {data.n8nWebhooksError}
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-5 py-2.5 text-left text-xs font-medium text-slate-500 uppercase">
                      Endpoint
                    </th>
                    <th className="px-5 py-2.5 text-left text-xs font-medium text-slate-500 uppercase">
                      Method
                    </th>
                    <th className="px-5 py-2.5 text-left text-xs font-medium text-slate-500 uppercase">
                      URL
                    </th>
                    <th className="px-5 py-2.5 text-left text-xs font-medium text-slate-500 uppercase">
                      Response
                    </th>
                    <th className="px-5 py-2.5 text-left text-xs font-medium text-slate-500 uppercase">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {webhooks.map((wh, i) => (
                    <WebhookRow key={i} wh={wh} />
                  ))}
                  {webhooks.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-5 py-4 text-center text-sm text-slate-400"
                      >
                        No webhook endpoints configured.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </SectionCard>

          {/* ═══ Facilities ═══ */}
          <SectionCard
            icon={Radio}
            iconColor="text-emerald-600"
            title="Facilities"
            colSpan={2}
            badge={
              <span className="text-xs text-green-600 font-medium">
                {
                  (data.facilities || []).filter((f) => f.status === "active")
                    .length
                }
                /{(data.facilities || []).length} active
              </span>
            }
          >
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-5 py-2.5 text-left text-xs font-medium text-slate-500 uppercase">
                      Facility
                    </th>
                    <th className="px-5 py-2.5 text-left text-xs font-medium text-slate-500 uppercase">
                      ID
                    </th>
                    <th className="px-5 py-2.5 text-left text-xs font-medium text-slate-500 uppercase">
                      State
                    </th>
                    <th className="px-5 py-2.5 text-left text-xs font-medium text-slate-500 uppercase">
                      Vertical
                    </th>
                    <th className="px-5 py-2.5 text-left text-xs font-medium text-slate-500 uppercase">
                      Subscription
                    </th>
                    <th className="px-5 py-2.5 text-left text-xs font-medium text-slate-500 uppercase">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(data.facilities || []).map((f) => (
                    <tr key={f.facility_id} className="hover:bg-slate-50">
                      <td className="px-5 py-3 text-sm font-medium text-slate-700">
                        {f.display_name || f.facility_name}
                      </td>
                      <td className="px-5 py-3 text-xs font-mono text-slate-400">
                        {f.facility_id}
                      </td>
                      <td className="px-5 py-3 text-sm text-slate-600">
                        {f.state || "—"}
                      </td>
                      <td className="px-5 py-3 text-sm text-slate-600 capitalize">
                        {f.vertical_id || "—"}
                      </td>
                      <td className="px-5 py-3">
                        <span
                          className={`text-xs font-medium capitalize ${f.subscription_status === "active" ? "text-green-600" : f.subscription_status === "suspended" ? "text-red-600" : "text-slate-500"}`}
                        >
                          {f.subscription_status || "—"}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <FacilityStatusBadge status={f.status} />
                      </td>
                    </tr>
                  ))}
                  {(data.facilities || []).length === 0 && (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-5 py-4 text-center text-sm text-slate-400"
                      >
                        No facilities found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </SectionCard>

          {/* ═══ Training Jobs ═══ */}
          {data.stats?.trainingJobs && (
            <SectionCard
              icon={Cpu}
              iconColor="text-amber-600"
              title="Training Jobs"
              colSpan={2}
            >
              <div className="px-5 py-4 flex flex-wrap gap-4">
                {Object.entries(data.stats.trainingJobs).map(
                  ([status, count]) => {
                    const colors = {
                      queued: "bg-slate-100 text-slate-700",
                      running: "bg-blue-100 text-blue-700",
                      completed: "bg-green-100 text-green-700",
                      failed: "bg-red-100 text-red-700",
                      cancelled: "bg-yellow-100 text-yellow-700",
                    };
                    return (
                      <div
                        key={status}
                        className={`px-4 py-3 rounded-lg ${colors[status] || "bg-slate-100 text-slate-700"}`}
                      >
                        <div className="text-2xl font-bold">{count}</div>
                        <div className="text-xs capitalize mt-0.5">
                          {status}
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            </SectionCard>
          )}
        </div>
      )}
    </div>
  );
};

// Facility status → badge styling (facility lifecycle, not service health)
const FacilityStatusBadge = ({ status }) => {
  const config = {
    active: { icon: CheckCircle, bg: "bg-green-100", text: "text-green-700" },
    provisioning: {
      icon: AlertTriangle,
      bg: "bg-yellow-100",
      text: "text-yellow-700",
    },
    pending: {
      icon: AlertTriangle,
      bg: "bg-yellow-100",
      text: "text-yellow-700",
    },
    suspended: { icon: XCircle, bg: "bg-red-100", text: "text-red-700" },
    inactive: { icon: XCircle, bg: "bg-slate-100", text: "text-slate-500" },
    archived: { icon: XCircle, bg: "bg-slate-100", text: "text-slate-500" },
  };
  const c = config[status] || {
    icon: AlertTriangle,
    bg: "bg-slate-100",
    text: "text-slate-500",
  };
  const Icon = c.icon;
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium capitalize ${c.bg} ${c.text}`}
    >
      <Icon className="w-3.5 h-3.5 mr-1" />
      {status || "unknown"}
    </span>
  );
};

// Webhook table row with expandable error
const WebhookRow = ({ wh }) => {
  const [expanded, setExpanded] = useState(false);
  const hasError = wh.error && isDown(wh.status);

  return (
    <>
      <tr
        className={`hover:bg-slate-50 ${hasError ? "cursor-pointer" : ""}`}
        onClick={() => hasError && setExpanded(!expanded)}
      >
        <td className="px-5 py-3">
          <div className="flex items-center space-x-2">
            {hasError &&
              (expanded ? (
                <ChevronDown className="w-3 h-3 text-slate-400" />
              ) : (
                <ChevronRight className="w-3 h-3 text-slate-400" />
              ))}
            <span className="text-sm font-medium text-slate-700">
              {wh.name}
            </span>
          </div>
          {wh.workflow && wh.workflow !== wh.name && (
            <div className="text-xs text-slate-400 mt-0.5 ml-5">
              {wh.workflow}
            </div>
          )}
        </td>
        <td className="px-5 py-3">
          <span
            className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${wh.method === "GET" ? "bg-blue-100 text-blue-600" : "bg-amber-100 text-amber-600"}`}
          >
            {wh.method}
          </span>
        </td>
        <td className="px-5 py-3 text-xs font-mono text-slate-400 max-w-md truncate">
          {wh.url}
        </td>
        <td className="px-5 py-3">
          <ResponseTime ms={wh.responseMs} />
        </td>
        <td className="px-5 py-3">
          <StatusBadge status={wh.status} />
        </td>
      </tr>
      {expanded && hasError && (
        <tr>
          <td colSpan={5} className="px-5 pb-3 bg-slate-50">
            <ErrorDetail error={wh.error} />
          </td>
        </tr>
      )}
    </>
  );
};

export default PlatformDashboard;
